import { useState } from 'react';

import { TpOrganizationInfo, TpPluginInfo } from '../../../api/models/admin';
import { sendEvent, useEventNotification } from '../../visual-analytics/hooks/events';
import { ModalPortal } from '../../../components/modal-portal/ModalPortal';
import styles from './AdminActions.module.scss';

const MSGOrganizationPluginsModal = 'showOrganizationPluginsModal';

interface TpOrganizationPluginsModalData {
  organization: TpOrganizationInfo;
  plugins: TpPluginInfo[];
  callBackOK: any;
  callBackCancel: any;
}

export function promptOrganizationPluginsModal(
  organization: TpOrganizationInfo,
  plugins: TpPluginInfo[],
): Promise<string[] | null> {
  return new Promise((resolve, reject) => {
    function callBackOK(data: string[]) {
      resolve(data);
    }

    function callBackCancel() {
      resolve(null);
    }

    const confirmYamlData: TpOrganizationPluginsModalData = {
      organization,
      plugins,
      callBackOK,
      callBackCancel,
    };
    sendEvent(MSGOrganizationPluginsModal, confirmYamlData);
  });
}

export function OrganizationsPluginModal() {
  const [modalOrganization, setModalOrganization] = useState<TpOrganizationInfo>({} as TpOrganizationInfo);
  const [modalPlugins, setModalPlugins] = useState<TpPluginInfo[]>([]);
  const [activePluginIds, setActivePluginIds] = useState<string[]>();
  const [modalData, setModalData] = useState<TpOrganizationPluginsModalData | null>(null);

  useEventNotification(
    MSGOrganizationPluginsModal,
    (tpe: string, contentInput: TpOrganizationPluginsModalData) => {
      const { organization, plugins } = contentInput;
      setModalData(contentInput);
      setModalOrganization(organization);
      setModalPlugins(plugins);
      if (organization.pluginIds) {
        setActivePluginIds(organization.pluginIds);
      } else {
        setActivePluginIds([]);
      }
    },
    [],
  );

  if ((!!activePluginIds && Object.keys(modalOrganization).length == 0) || modalPlugins.length === 0) return null;

  const onDragStart = (e, id) => {
    e.dataTransfer.setData('id', id);
  };

  const onDrop = (e, isActive: boolean) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('id');
    setActivePluginIds((prev) => {
      return isActive ? [...new Set([...prev, id])] : prev.filter((pluginId) => pluginId !== id);
    });
  };

  const renderPlugins = (plugins: TpPluginInfo[], isActive: boolean) => (
    <div className={styles.pluginColumn} onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, isActive)}>
      <h3>{isActive ? 'Active Plugins' : 'Inactive Plugins'}</h3>
      {plugins.map((plugin) => (
        <div
          key={plugin.identifier}
          draggable
          onDragStart={(e) => onDragStart(e, plugin.identifier)}
          className={styles.pluginCard}
        >
          {plugin.prettyName}
        </div>
      ))}
    </div>
  );

  return (
    <ModalPortal
      close={() => {
        setModalOrganization({} as TpOrganizationInfo);
        setModalPlugins([]);
      }}
    >
      {!!activePluginIds && !!modalData && (
        <div className={styles.modalWrapper}>
          <h2 className={styles.modalHeader}>Modify Plugins</h2>
          <div className={styles.modalContent}>
            {renderPlugins(
              modalPlugins.filter((plugin) => activePluginIds.includes(plugin.identifier)),
              true,
            )}
            {renderPlugins(
              modalPlugins.filter((plugin) => !activePluginIds.includes(plugin.identifier)),
              false,
            )}
          </div>
          <div className={styles.buttonSection}>
            <button
              onClick={() => {
                modalData.callBackCancel();
                setModalData(null);
                setModalOrganization({} as TpOrganizationInfo);
                setModalPlugins([]);
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                modalData.callBackOK(activePluginIds);
                setModalData(null);
                setModalOrganization({} as TpOrganizationInfo);
                setModalPlugins([]);
              }}
            >
              Save Plugins
            </button>
          </div>
        </div>
      )}
    </ModalPortal>
  );
}
